import type { TaskType } from 'api-validations/ag-concepts'

/**
 * StreamGroup partition model.
 *
 * A `StreamGroup` is a set of Streams in the same Field + Season + Task that are
 * merged into a single map view. Members are kept oldest → newest under the
 * `rankOf` ordering the caller supplies; the **primary** is the last member,
 * `streamIds.at(-1)`. The primary is the focal machine for single-machine
 * surfaces (Diagnose / Config) and the write target for new Scouting notes. It
 * is always derived from membership, never pinned: it changes only when
 * membership changes.
 *
 * A pane's partition for a Task is an exhaustive, disjoint `StreamGroup[]` — every
 * Stream in the Task belongs to exactly one group. The functions here are pure
 * transforms over a partition; they never touch the store, so they are unit
 * testable in isolation. The store holds only *custom* partitions (the result of
 * user edits), so an empty partition map means "no customisation yet".
 */
export type StreamGroup = { streamIds: string[] }

export type StreamPartition = StreamGroup[]

/** What a partition covers. Two passes can only share a group within one of these. */
export type StreamPartitionScope = {
  client: string
  farm: string
  field: string
  season: number
  cropKey: string
  task: TaskType
}

/**
 * The key a pane's partitions are stored under. Shared by the picker and the
 * resolvers because a partition written under one spelling of the scope and read
 * under another is simply lost.
 */
export const partitionKeyOf = ({
  client,
  farm,
  field,
  season,
  cropKey,
  task
}: StreamPartitionScope): string => `${client}|${farm}|${field}|${season}|${cropKey}|${task}`

/** The most-recently-authored member of a group, or null for an empty group. */
export const primaryOf = (group: StreamGroup): string | null => group.streamIds.at(-1) ?? null

/** The group within a partition that contains `streamId`, or null. */
export const groupContaining = (partition: StreamPartition, streamId: string): StreamGroup | null =>
  partition.find((g) => g.streamIds.includes(streamId)) ?? null

/**
 * The group containing `streamId` across every partition stored for a pane
 * (partitions are disjoint, so the match is unique). Used to resolve the active
 * group from just the selected stream id, without needing its Task.
 */
export const findGroupAcross = (
  byKey: Record<string, StreamPartition | undefined>,
  streamId: string
): StreamGroup | null => {
  for (const partition of Object.values(byKey)) {
    if (partition === undefined) continue
    const group = groupContaining(partition, streamId)
    if (group) return group
  }
  return null
}

/**
 * Merging more streams costs the map and the backend more, so a default group
 * stops growing at this size. Users can still merge more by hand.
 */
const MAX_DEFAULT_GROUP_SIZE = 4

/**
 * Build the default partition: Streams authored on the same calendar day form
 * one group, up to `MAX_DEFAULT_GROUP_SIZE`. Groups are returned oldest first,
 * members within a group likewise. Input need not be pre-sorted.
 */
export const defaultPartition = (
  streams: readonly { streamId: string; authoredAt: Date }[]
): StreamPartition => {
  // Break exact-timestamp ties by streamId so grouping (and the derived
  // primary) is deterministic regardless of input order.
  const sorted = [...streams].sort(
    (a, b) =>
      a.authoredAt.getTime() - b.authoredAt.getTime() || a.streamId.localeCompare(b.streamId)
  )
  const partition: StreamPartition = []
  let previousDay: string | null = null
  for (const { streamId, authoredAt } of sorted) {
    const day = dayKey(authoredAt)
    const last = partition.at(-1)
    if (
      last !== undefined &&
      day === previousDay &&
      last.streamIds.length < MAX_DEFAULT_GROUP_SIZE
    ) {
      last.streamIds.push(streamId)
    } else {
      partition.push({ streamIds: [streamId] })
    }
    previousDay = day
  }
  return partition
}

/**
 * Picks the time a stream is grouped and ordered by. A finished stream uses its
 * finish time. A stream still in progress has none yet, so it uses the last
 * time the live roster heard from it, or failing that its record's last change.
 */
export const authoredAtOf = ({
  stream,
  finishedAt,
  liveStreams
}: {
  stream: { streamId: string; lastModified: Date }
  finishedAt: ReadonlyMap<string, Date> | undefined
  liveStreams: ReadonlyMap<string, { lastModified: Date }>
}): Date =>
  finishedAt?.get(stream.streamId) ??
  liveStreams.get(stream.streamId)?.lastModified ??
  stream.lastModified

/**
 * Remove `streamId` from its current group and spawn a singleton group for it.
 * No-op if the stream is the sole member of its group (the spec forbids
 * unchecking a single-member group). Stays exhaustive and disjoint, and is
 * re-sorted by `rankOf` (so member/group order matches whatever ordering the
 * caller supplies).
 */
export const removeStream = (
  partition: StreamPartition,
  streamId: string,
  rankOf: (id: string) => number
): StreamPartition => {
  const source = groupContaining(partition, streamId)
  if (source === null || source.streamIds.length === 1) return partition

  // Only the source group changes; untouched groups pass through by reference —
  // `sortPartition` is the single point that allocates fresh group objects.
  const next = partition
    .map((g) => (g === source ? { streamIds: g.streamIds.filter((id) => id !== streamId) } : g))
    .concat({ streamIds: [streamId] })
  return sortPartition(next, rankOf)
}

/**
 * Move `streamId` out of its current group into the group that currently
 * contains `destMember` (any member of the destination — typically its primary).
 * This is a move, not a copy: the source group shrinks and is dropped if it
 * empties. No-op if source and destination are the same group. Stays exhaustive
 * and disjoint, and is re-sorted by `rankOf`.
 */
export const moveStream = (
  partition: StreamPartition,
  streamId: string,
  destMember: string,
  rankOf: (id: string) => number
): StreamPartition => {
  if (streamId === destMember) return partition

  const source = groupContaining(partition, streamId)
  const dest = groupContaining(partition, destMember)
  if (source === null || dest === null || source === dest) return partition

  // Only source and dest change; others pass through by reference (sortPartition
  // copies). No in-place mutation of the input partition.
  const next = partition
    .map((g) => {
      if (g === source) return { streamIds: g.streamIds.filter((id) => id !== streamId) }
      if (g === dest) return { streamIds: [...g.streamIds, streamId] }
      return g
    })
    .filter((g) => g.streamIds.length > 0)
  return sortPartition(next, rankOf)
}

// getMonth() is 0-indexed — intentional, not a bug.
const dayKey = (date: Date): string => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

/**
 * Normalize a partition: sort members within each group, then groups by their
 * earliest member — both by `rankOf`. Returns freshly-allocated group objects
 * and arrays, so callers may build the input from group objects shared with the
 * original partition (this is the single copy/normalization point).
 */
const sortPartition = (
  partition: StreamPartition,
  rankOf: (id: string) => number
): StreamPartition =>
  partition
    .map((g) => ({ streamIds: [...g.streamIds].sort((a, b) => rankOf(a) - rankOf(b)) }))
    .sort((a, b) => rankOf(a.streamIds[0]) - rankOf(b.streamIds[0]))
